import { Controller } from "@hotwired/stimulus";

// ✨ per-field copy refinement in the page builder. Wraps one
// text/textarea setting input; posts its current value + an instruction
// to the refine endpoint and writes the rewritten text back into the
// input, dispatching "input" so the page editor's auto-save (draft)
// picks the change up like any manual edit.
//
// The endpoint URL, page slug, and CSRF token live on the page-editor
// root (data-oscar-refine-url / -page / -csrf) so each field only
// carries its own labels.
export default class extends Controller {
  static targets = ["input", "panel", "instruction", "error", "submit"];
  static values = { label: String, block: String };

  toggle() {
    this.panelTarget.classList.toggle("hidden");
    if (!this.panelTarget.classList.contains("hidden")) {
      this.instructionTarget.focus();
    }
  }

  preset(event) {
    this.instructionTarget.value = event.params.instruction;
    this.submit();
  }

  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault();
      this.submit();
    }
  }

  async submit() {
    const root = this.element.closest("[data-oscar-refine-url]");
    if (!root || this.busy) return;

    const instruction = this.instructionTarget.value.trim();
    if (!instruction) {
      this.instructionTarget.focus();
      return;
    }

    this.setBusy(true);
    this.errorTarget.classList.add("hidden");

    try {
      const response = await fetch(root.dataset.oscarRefineUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": root.dataset.oscarRefineCsrf,
          Accept: "application/json",
        },
        body: JSON.stringify({
          text: this.inputTarget.value,
          instruction,
          field_label: this.labelValue,
          block_label: this.blockValue,
          page_slug: root.dataset.oscarRefinePage,
        }),
      });
      const data = await response.json();

      if (response.ok && data.text) {
        this.inputTarget.value = data.text;
        this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
        this.instructionTarget.value = "";
        this.panelTarget.classList.add("hidden");
      } else {
        this.showError(data.error || "Oscar couldn't rewrite that — try again.");
      }
    } catch {
      this.showError("Network hiccup — try again.");
    } finally {
      this.setBusy(false);
    }
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.classList.remove("hidden");
  }

  setBusy(busy) {
    this.busy = busy;
    this.submitTarget.disabled = busy;
    this.submitTarget.textContent = busy ? "Rewriting…" : "Rewrite";
  }
}
